/**
 * Authentication context for managing user authentication state
 */

import { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback, ReactNode } from 'react';
import { exitImpersonation, getImpersonation, isImpersonating } from '@/lib/impersonation';
import { UserProfile, UserRegister, UserLogin, UserPreferences } from '@/services/api';
import { apiService } from '@/services/api';
import { apiClient, resetAuthFailureState } from '@/services/apiClient';
import { useTranslation } from 'react-i18next';
import { preCacheForOffline } from '@/services/offlineApiClient';

import { config as productConfig } from '@/src/config';
import { privacyAPI } from '@/services/api/privacy';
import {
  IDLE_TIMEOUT_CHANGED_EVENT,
  IDLE_TIMEOUT_STORAGE_KEY,
  idleRemainingSeconds,
  isIdleExpired,
  latestActivityMs,
  normalizeIdleTimeoutMinutes,
  readCachedIdleTimeoutMinutes,
  readSharedActivityMs,
  warningLeadSeconds,
  writeCachedIdleTimeoutMinutes,
} from '@/lib/sessionIdleTimeout';
import {
  broadcastSignedOut,
  isSessionActive,
  registerLocalSignOutHandler,
  setSessionActive,
  startSessionSignOutListener,
  type SignOutReason,
} from '@/lib/sessionBroadcast';
import { dropCachedAuthToken } from '@/lib/authFetch';
import { clearStaffReturn } from '@/lib/staffReturn';
import { clearApiCache } from '@/services/offlineApiClient';
import { queryClient } from '@/lib/queryClient';
import { isTopLevelPopoutWindow } from '@/lib/desktopWindow';
import { PopoutSignedOutScreen } from '@/components/popout/PopoutSignedOutScreen';
interface AuthContextType {
  user: UserProfile | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  preferences: UserPreferences | null;
  login: (loginData: UserLogin) => Promise<boolean>;
  register: (userData: UserRegister) => Promise<boolean>;
  logout: (tokenOverride?: string, opts?: LogoutOptions) => void;
  clearError: () => void;
  timeRemaining: number;
  /** The active org's idle timeout, in seconds (default 8 hours). */
  sessionTimeoutSeconds: number;
  /** Show the "session expiring" warning at this many seconds remaining. */
  sessionWarningSeconds: number;
  extendSession: () => void;
  setUser: (user: UserProfile | null) => void;
  setToken: (token: string | null) => void;
  completeLogin: (token: string, user: UserProfile, refreshToken?: string, expiresIn?: number) => void;
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>;
  refreshPreferences: () => Promise<void>;
}

export interface LogoutOptions {
  scope?: 'all' | 'session';
  refreshToken?: string;
  /** Why the session ended; sent to the other windows with the sign-out signal. */
  reason?: SignOutReason;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Session inactivity timeout: resets on user activity, logout when idle. The
// length is per organization (lib/sessionIdleTimeout.ts); 8 hours by default.

/**
 * Last activity in ANY window of this browser/app. Every window writes
 * session_start on activity, so with pop-out windows and tabs (and the
 * iframes of a tabbed window) a window the user is not looking at must not
 * sign everyone out just because it saw no activity itself.
 */
function latestActivity(local: number): number {
  return latestActivityMs(local, readSharedActivityMs());
}

/**
 * Expiry (ms epoch) from a JWT's own `exp` claim, or null if unreadable.
 *
 * Used as the fallback for a missing `token_expires_at`. Decode-only — this
 * makes no trust decision, it just reads a timestamp the server already signed.
 */
function accessTokenExpiryMs(token: string | null): number | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json)?.exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { i18n } = useTranslation();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  // A ref, not state: the timers below are armed from closures of older
  // renders, and a cleared-but-stale handle used to leave a backstop timer
  // running after sign-out (TRUS-638 — a stale timer would now broadcast).
  const sessionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A top-level pop-out that has signed out: it closes itself, or shows a
  // "you've been signed out" screen in place of the whole app (TRUS-638).
  const [popoutSignedOut, setPopoutSignedOut] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  // The active org's idle timeout. The ref is what timers read (they are
  // created once and outlive renders); the state re-renders the warning.
  const [idleTimeoutMinutes, setIdleTimeoutMinutesState] = useState<number>(() => readCachedIdleTimeoutMinutes());
  const idleTimeoutMinutesRef = useRef<number>(idleTimeoutMinutes);
  const applyIdleTimeoutMinutes = (minutes: number) => {
    const next = normalizeIdleTimeoutMinutes(minutes);
    idleTimeoutMinutesRef.current = next;
    setIdleTimeoutMinutesState(next);
  };

  // Session management functions
  const startSessionTimeout = () => {
    // Clear existing timeout
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }

    // Set session start time
    const startTime = Date.now();
    setSessionStartTime(startTime);
    const timeoutMinutes = idleTimeoutMinutesRef.current;
    setTimeRemaining(timeoutMinutes * 60);

    // Update localStorage with new session start time
    localStorage.setItem('session_start', startTime.toString());

    // Set inactivity timeout
    // A backstop only: the per-second check below is what normally signs out,
    // and it always uses the CURRENT org timeout (this one may be stale if the
    // org's setting arrived after the timer was armed).
    const timeout = setTimeout(() => {
      // Signed out meanwhile (here or in another window): nothing to expire.
      if (!isSessionActive()) return;
      // Another window saw activity since, or the timeout grew: the per-second check takes over.
      if (!isIdleExpired(latestActivity(startTime), Date.now(), idleTimeoutMinutesRef.current)) return;
      console.log(`Session expired after ${idleTimeoutMinutesRef.current} minutes of inactivity`);
      logout(undefined, { reason: 'idle' });
    }, timeoutMinutes * 60 * 1000);

    sessionTimeoutRef.current = timeout;
  };

  const clearSessionTimeout = () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
  };

  const resetSessionTimeout = () => {
    if (user) {
      startSessionTimeout();
    }
  };

  const extendSession = () => {
    if (user) {
      startSessionTimeout();
    }
  };

  // Load token from localStorage on mount with proper session management
  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    const sessionStart = localStorage.getItem('session_start');

    // If we have a token, try to verify it
    if (savedToken) {
      // Check session expiration if session_start exists
      if (sessionStart) {
        const sessionStartTime = parseInt(sessionStart);
        const currentTime = Date.now();

        // Check if session has expired (against the last org timeout this browser saw)
        if (isIdleExpired(sessionStartTime, currentTime, idleTimeoutMinutesRef.current)) {
          // Session expired, clear auth state
          console.log('🔐 Session expired, clearing auth state');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('session_start');
          setLoading(false);
          return;
        }
      } else {
        // No session_start stored - create one now to maintain session tracking
        // This handles cases where user has token but missing session_start (e.g., migration)
        localStorage.setItem('session_start', Date.now().toString());
      }

      // Token exists and session hasn't expired - verify with backend
      setToken(savedToken);
      verifyToken(savedToken);
      return;
    }

    // No valid session found
    setLoading(false);
  }, []);

  // Tell the cross-window sign-out module whether this window is signed in.
  useEffect(() => {
    setSessionActive(!!user);
  }, [user]);

  // Sign-out in any other window signs this one out (TRUS-638).
  useEffect(() => {
    const unregister = registerLocalSignOutHandler((reason) => signOutLocallyRef.current(reason));
    const stop = startSessionSignOutListener();
    return () => {
      stop();
      unregister();
    };
  }, []);

  // Listen for auth:logout events dispatched by the apiClient interceptor on 401
  useEffect(() => {
    const handleForceLogout = (e: Event) => {
      console.warn('🔐 Received auth:logout event from API interceptor');
      // The interceptor forwards the token it was using so the backend logout
      // can still blacklist it — localStorage has already been cleared.
      const detail = (e as CustomEvent)?.detail ?? {};
      const token = detail.token as string | undefined;
      // Session scope is the default now, but the refresh token must come from the
      // event: the interceptor cleared storage before dispatching, so reading it here
      // would find nothing and the backend would have no family to scope the revoke to.
      logout(token, { refreshToken: detail.refreshToken as string | undefined, reason: 'unauthorized' });
    };
    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, []);

  // Activity listeners to reset session timeout
  useEffect(() => {
    if (!user) return;

    const handleUserActivity = () => {
      resetSessionTimeout();
    };

    // Add event listeners for user activity (throttled to avoid excessive calls)
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledHandler = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        handleUserActivity();
        throttleTimeout = null;
      }, 1000); // Throttle to once per second
    };
    
    events.forEach(event => {
      document.addEventListener(event, throttledHandler, true);
    });

    // Cleanup event listeners
    return () => {
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      events.forEach(event => {
        document.removeEventListener(event, throttledHandler, true);
      });
    };
  }, [user]);

  // Timer effect to update time remaining
  useEffect(() => {
    if (!sessionStartTime || !user) return;

    const interval = setInterval(() => {
      const remaining = idleRemainingSeconds(
        latestActivity(sessionStartTime),
        Date.now(),
        idleTimeoutMinutesRef.current,
      );
      setTimeRemaining(remaining);

      if (remaining === 0) {
        logout(undefined, { reason: 'idle' });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [sessionStartTime, user]);

  // Load the active org's idle timeout at sign-in and on every org switch.
  // GET /privacy-policy is readable by any member of the org; it resolves an
  // unset value to the 480-minute default. A failure keeps the cached value.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const load = async (rearm: boolean) => {
      let orgId: string | null = null;
      try {
        orgId = localStorage.getItem('active_cbo_id');
      } catch {
        orgId = null;
      }
      const cboId = orgId ? parseInt(orgId, 10) : NaN;
      if (!Number.isFinite(cboId)) return;
      try {
        const policy = await privacyAPI.getOrgPrivacyPolicy(cboId);
        if (cancelled) return;
        // Only adopt the answer if the org is still the one we asked about.
        if (localStorage.getItem('active_cbo_id') !== orgId) return;
        const minutes = normalizeIdleTimeoutMinutes(policy?.idle_timeout_minutes);
        writeCachedIdleTimeoutMinutes(minutes);
        applyIdleTimeoutMinutes(minutes);
      } catch (err) {
        console.warn('[Auth] Could not load the org idle timeout; keeping the current one', err);
      }
      // Switching org in THIS window is user activity: re-arm with the new
      // org's timeout. (A switch seen via another window's storage event is
      // not — re-arming there would extend the session without activity.)
      if (!cancelled && rearm) startSessionTimeout();
    };

    const onOrgChanged = () => {
      void load(true);
    };
    const onTimeoutSaved = () => {
      void load(false);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === IDLE_TIMEOUT_STORAGE_KEY) {
        // Another window (a pop-out or tab) loaded the org's value: adopt it so
        // every window enforces the same limit.
        applyIdleTimeoutMinutes(normalizeIdleTimeoutMinutes(e.newValue));
      } else if (e.key === 'active_cbo_id') {
        void load(false);
      }
    };

    void load(false);
    window.addEventListener('organizationChanged', onOrgChanged);
    window.addEventListener(IDLE_TIMEOUT_CHANGED_EVENT, onTimeoutSaved);
    window.addEventListener('storage', onStorage);
    return () => {
      cancelled = true;
      window.removeEventListener('organizationChanged', onOrgChanged);
      window.removeEventListener(IDLE_TIMEOUT_CHANGED_EVENT, onTimeoutSaved);
      window.removeEventListener('storage', onStorage);
    };
  }, [user?.id]);

  // Cleanup session timeout on unmount
  useEffect(() => {
    return () => {
      clearSessionTimeout();
    };
  }, []);

  // Apply theme to document
  const applyTheme = (theme: string) => {
    console.log('🎨 applyTheme called with theme:', theme);
    const htmlElement = document.documentElement;

    if (theme === 'system') {
      // Follow system preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const isDark = mediaQuery.matches;
      console.log('🎨 System theme detected:', isDark ? 'dark' : 'light');
      htmlElement.classList.toggle('dark', isDark);
    } else {
      // Apply explicit theme
      const isDark = theme === 'dark';
      console.log('🎨 Applying theme:', theme, '| Setting dark class:', isDark);
      htmlElement.classList.toggle('dark', isDark);
    }
    console.log('🎨 HTML element classes after apply:', htmlElement.className);
  };

  const fetchUserPreferences = async (authToken: string) => {
    try {
      const response = await apiClient.get('/api/user/preferences');
      console.log('👤 User preferences fetched:', response.data);
      setPreferences(response.data);
      // Update i18n language
      if (response.data.language_code) {
        i18n.changeLanguage(response.data.language_code);
      }
      // Apply theme
      if (response.data.theme) {
        console.log('👤 Applying theme from preferences:', response.data.theme);
        applyTheme(response.data.theme);
      } else {
        console.log('⚠️ No theme in preferences, using default');
      }
    } catch (err) {
      console.error('Error fetching user preferences:', err);
      // Set default preferences
      setPreferences({
        user_id: 0,
        language_code: 'en',
        // TRUS-695: unknown -> empty, so time display falls back to the org's
        // zone, then the browser's (never a hard-coded Pacific).
        timezone: '',
        date_format: 'MM/DD/YYYY',
        time_format: '12h',
        theme: 'light',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
  };

  const verifyToken = async (tokenToVerify: string, retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 3000; // 3 seconds between retries
    // How many attempts may hold the app on its full-screen spinner.
    //
    // app.tsx renders a blocking "Loading..." screen while `loading` is true,
    // so the retry ladder used to hide the login page for the whole ladder
    // (4 attempts x 3s, plus each request's own timeout). Users with a stale
    // token on a slow connection reported exactly that as "can't see the login
    // page". One attempt absorbs a deploy blip; after that we release the UI so
    // there is always an actionable screen, and the remaining retries continue
    // in the background — if one succeeds, setUser() swaps the app into the
    // authenticated view. The token is deliberately kept either way.
    const UI_BLOCKING_ATTEMPTS = 1;
    let pendingRetry = false;
    const releaseLoadingGate = () => setLoading(false);

    try {
      const response = await apiService.getCurrentUser(tokenToVerify);

      if (response.success && response.data) {
        setUser(response.data);
        setError(null);
        // Seed active_cbo_id from /me ONLY when it's missing — required for
        // brand-new Triveny logins whose tenant pins a cbo_id in
        // platform_tenants.settings, since they start with no localStorage.
        // Must NOT overwrite an existing value: WeaveIn users who switched
        // orgs (e.g. Maui LTRG) would otherwise snap back to their home
        // tenant on every refresh.
        if (localStorage.getItem('active_cbo_id') == null) {
          const meCboId = (response.data as any)?.cbo_id;
          if (meCboId !== undefined && meCboId !== null) {
            localStorage.setItem('active_cbo_id', String(meCboId));
          }
        }
        // Fetch user preferences
        await fetchUserPreferences(tokenToVerify);
        // Start session timeout when user is verified
        startSessionTimeout();
      } else {
        // Check if error is transient (backend restarting during deployment)
        const errorMsg = response.error || '';
        const isTransient =
          errorMsg.includes('HTTP 502') ||
          errorMsg.includes('HTTP 503') ||
          errorMsg.includes('HTTP 504') ||
          errorMsg.includes('Failed to fetch') ||
          errorMsg.includes('NetworkError') ||
          errorMsg.includes('CORS') ||
          errorMsg.includes('ERR_CONNECTION') ||
          errorMsg.includes('ECONNREFUSED');

        if (isTransient && retryCount < MAX_RETRIES) {
          console.warn(`🔄 Backend temporarily unavailable (attempt ${retryCount + 1}/${MAX_RETRIES}), retrying in ${RETRY_DELAY_MS / 1000}s...`);
          pendingRetry = true;
          // Keep the token — the backend is just restarting
          if (retryCount >= UI_BLOCKING_ATTEMPTS) releaseLoadingGate();
          setTimeout(() => verifyToken(tokenToVerify, retryCount + 1), RETRY_DELAY_MS);
          return;
        }

        // Transient error exhausted retries — keep session alive, backend will come back
        if (isTransient) {
          console.warn('⚠️ Backend unavailable after all retries — keeping session alive');
          setError('Backend is temporarily unavailable. Your session is preserved — please wait a moment and try again.');
          return;
        }

        // Definitive auth failure (401, 403) — clear session
        localStorage.removeItem('auth_token');
        localStorage.removeItem('session_start');
        setToken(null);
        setUser(null);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '';
      const isTransient =
        errorMsg.includes('CORS') ||
        errorMsg.includes('Failed to fetch') ||
        errorMsg.includes('NetworkError') ||
        errorMsg.includes('ERR_CONNECTION') ||
        errorMsg.includes('ECONNREFUSED');

      if (isTransient && retryCount < MAX_RETRIES) {
        console.warn(`🔄 Network error during token verification (attempt ${retryCount + 1}/${MAX_RETRIES}), retrying...`);
        pendingRetry = true;
        if (retryCount >= UI_BLOCKING_ATTEMPTS) releaseLoadingGate();
        setTimeout(() => verifyToken(tokenToVerify, retryCount + 1), RETRY_DELAY_MS);
        return;
      }

      if (isTransient) {
        console.warn('⚠️ Network error persists after all retries — keeping session alive');
        setError('Unable to connect to server. Your session is preserved — please check your connection.');
        return;
      }

      console.error('🔐 Token verification failed:', errorMsg);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('session_start');
      setToken(null);
      setUser(null);
    } finally {
      if (!pendingRetry) {
        setLoading(false);
      }
    }
  };

  // API base URL for raw fetch calls (avoids axios interceptor loops)
  // On dev.weavein.ai, use relative URLs so Vercel proxy rewrites handle routing (avoids CORS)
  const API_BASE_URL = (typeof window !== 'undefined' && window.location.hostname === 'dev.weavein.ai')
    ? ''
    : (productConfig.weaveinApi.baseUrl ?? 'https://weavein-api.onrender.com').trim();

  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        console.warn('🔐 Refresh token rejected, clearing refresh state');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expires_at');
        return false;
      }

      const data = await response.json();
      localStorage.setItem('auth_token', data.access_token);
      setToken(data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      if (data.expires_in) {
        localStorage.setItem('token_expires_at', (Date.now() + data.expires_in * 1000).toString());
      }
      return true;
    } catch (err) {
      console.error('🔐 Token refresh failed:', err);
      return false;
    }
  }, []);

  // Proactive token refresh: refresh 5 minutes before access token expiry
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user || !token) {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }

    const scheduleRefresh = () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      // TRUS-747: a View as user token is never refreshed. At its expiry the
      // browser returns to the super admin.
      const impersonation = getImpersonation();
      if (impersonation) {
        refreshTimerRef.current = setTimeout(
          () => void exitImpersonation('expired'),
          Math.max(0, impersonation.expiresAt - Date.now()),
        );
        return;
      }

      // token_expires_at is written only when a login path supplies expires_in.
      // A path that forgets it used to disable proactive refresh ENTIRELY and
      // silently — the SAML callback did exactly that, so SSO sessions never
      // scheduled a refresh and just took a hard 401 at expiry. Fall back to the
      // access token's own `exp` claim, which is authoritative and always there,
      // so a missing value degrades to "read it off the token" instead of
      // "never refresh".
      let expiresAtStr = localStorage.getItem('token_expires_at');
      if (!expiresAtStr) {
        const derived = accessTokenExpiryMs(localStorage.getItem('auth_token'));
        if (derived) {
          expiresAtStr = String(derived);
          localStorage.setItem('token_expires_at', expiresAtStr);
          console.warn('🔐 token_expires_at was missing; derived it from the token exp claim');
        }
      }
      if (!expiresAtStr) return;

      const expiresAt = parseInt(expiresAtStr);
      const refreshAt = expiresAt - 5 * 60 * 1000; // 5 minutes before expiry
      const delay = refreshAt - Date.now();

      if (delay <= 0) {
        // Token about to expire or already expired — refresh now
        refreshAccessToken().then((success) => {
          if (success) {
            scheduleRefresh();
          } else {
            logout(undefined, { reason: 'refresh-failed' });
          }
        });
        return;
      }

      refreshTimerRef.current = setTimeout(() => {
        refreshAccessToken().then((success) => {
          if (success) {
            scheduleRefresh();
          } else {
            logout(undefined, { reason: 'refresh-failed' });
          }
        });
      }, delay);
    };

    scheduleRefresh();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [user, token, refreshAccessToken]);

  // Identify the signed-in user to the embedded support widget so chat + tickets
  // attach their identity/context (and the ticket form stops asking name/email).
  // Sets config.user for a not-yet-initialized widget AND pushes to a live one,
  // covering both load orders (the widget script is async).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const widgetUser = user ? { id: user.id, name: user.name, email: user.email, role: user.role } : null;
    const w = window as any;
    w.TrustSupportConfig = { ...(w.TrustSupportConfig || {}), user: widgetUser };
    if (w.TrustSupportWidget && typeof w.TrustSupportWidget.setUser === 'function') {
      try {
        w.TrustSupportWidget.setUser(widgetUser);
      } catch {
        /* widget not ready / no-op */
      }
    }
  }, [user]);

  const login = async (loginData: UserLogin): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.login(loginData);
      
      if (response.success && response.data) {
        const { access_token, refresh_token, user: userData, expires_in } = response.data;

        // Store token and session start time in localStorage
        localStorage.setItem('auth_token', access_token);
        if (refresh_token) {
          localStorage.setItem('refresh_token', refresh_token);
        }
        if (expires_in) {
          localStorage.setItem('token_expires_at', (Date.now() + expires_in * 1000).toString());
        }
        localStorage.setItem('session_start', Date.now().toString());
        
        // Update state
        setToken(access_token);
        setUser({
          ...userData,
          created_at: (userData as any).created_at || new Date().toISOString(),
          last_login: (userData as any).last_login || null
        });
        setError(null);

        // Sync active_cbo_id to match the authenticated user's org
        // Prevents stale cbo_id from a previous session/org leaking cross-tenant data
        if (userData.cbo_id) {
          localStorage.setItem('active_cbo_id', String(userData.cbo_id));
        } else {
          localStorage.removeItem('active_cbo_id');
        }
        // Clear stale tenant slug — will be re-resolved from the new org context
        localStorage.removeItem('platform_tenant_slug');

        // Start session timeout
        startSessionTimeout();

        // Pre-cache data for offline use (non-blocking)
        const orgId = localStorage.getItem('active_cbo_id');
        if (orgId) {
          preCacheForOffline(parseInt(orgId, 10)).catch((err) => {
            console.warn('[Auth] Pre-caching for offline failed:', err);
          });
        }

        return true;
      } else {
        setError(response.error || 'Login failed');
        return false;
      }
    } catch (err) {
      // Handle CORS and network errors more gracefully
      if (err instanceof Error) {
        if (err.message.includes('CORS') || err.message.includes('Failed to fetch')) {
          setError('Backend service is not available. Please try again later or contact support.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Login failed');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: UserRegister): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.register(userData);
      
      if (response.success && response.data) {
        // Registration successful, now login
        const loginData: UserLogin = {
          email: userData.email,
          password: userData.password
        };
        
        return await login(loginData);
      } else {
        setError(response.error || 'Registration failed');
        return false;
      }
    } catch (err) {
      // Handle CORS and network errors more gracefully
      if (err instanceof Error) {
        if (err.message.includes('CORS') || err.message.includes('Failed to fetch')) {
          setError('Backend service is not available. Please try again later or contact support.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Registration failed');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * @param tokenOverride the access token to blacklist. Supplied by the
   *   apiClient 401 interceptor, which has already cleared localStorage by the
   *   time it dispatches auth:logout.
   * @param opts.scope Defaults to 'session': logging out ends THIS device's session
   *   and leaves the user signed in everywhere else. That is the product decision —
   *   signing out of a laptop should not sign you out of your phone, and a 401 one tab
   *   could not recover from certainly should not.
   *
   *   'all' revokes every refresh token the user holds, on every device. Nothing calls
   *   it today; it stays as the deliberate way to ask for a full sign-out (a lost
   *   device, a forced global revoke) so that intent has to be stated, not defaulted
   *   into. The backend also treats a request with NO scope as 'all', which is how
   *   clients that predate this keep their old behaviour.
   * @param opts.refreshToken the ending session's refresh token — what tells the
   *   backend WHICH family to revoke. Defaults to the one in storage; the 401
   *   interceptor passes it explicitly because it has already cleared storage by then.
   *   Without one the backend revokes nothing further, which is correct: blacklisting
   *   the access token has already ended this session.
   */
  const logout = (tokenOverride?: string, opts?: LogoutOptions) => {
    // TRUS-747: signing out while viewing as another user ends THAT session and
    // returns to the super admin. It must never reach /api/auth/logout, which
    // would revoke the viewed user's own devices.
    if (isImpersonating()) {
      void exitImpersonation(opts?.reason || 'logout');
      return;
    }
    // Several menus wire `onLogout={logout}` straight to a click handler, which
    // passes the click event here. Only a string is a token.
    if (typeof tokenOverride !== 'string') tokenOverride = undefined;
    // Was this window signed in? Read before anything is cleared. A window that
    // was not (a stale timer, a second click) has no session to end for others.
    const wasActive = isSessionActive();
    // From here on, requests from this window are not "signed in but token
    // missing" (see sessionBroadcast.checkSessionTokenPresent) — they are after
    // a sign-out this window started.
    setSessionActive(false);

    // Call backend logout to blacklist token (fire-and-forget, don't block UI).
    //
    // The token is passed EXPLICITLY rather than left to the request
    // interceptor to read from localStorage. When logout is triggered by the
    // apiClient 401 interceptor, that interceptor has already removed
    // `auth_token`, so the interceptor-attached header was empty and this POST
    // arrived with no credentials — the backend answered 401 and never
    // blacklisted anything. Production showed the result plainly: an account
    // with dozens of logouts had zero rows in `token_blacklist`.
    const currentToken = tokenOverride ?? localStorage.getItem('auth_token');
    // The refresh token must be read BEFORE the removals below, same reason the
    // access token is passed in explicitly.
    const currentRefreshToken = opts?.refreshToken ?? localStorage.getItem('refresh_token') ?? undefined;
    const scope = opts?.scope ?? 'session';
    if (currentToken) {
      apiClient
        .post(
          '/api/auth/logout',
          scope === 'session'
            ? { scope: 'session', refresh_token: currentRefreshToken }
            : { scope: 'all' },
          {
            headers: { Authorization: `Bearer ${currentToken}` },
            // This request is *expected* to be the last act of a dead session;
            // a 401 here must not re-enter the logout cascade.
            skipAuthLogout: true,
          } as any,
        )
        .catch((err) => {
          console.warn('Backend logout failed (non-blocking):', err);
        });
    }

    // Stop browser pushes to this computer (TRUS-668): deregister + unsubscribe.
    // Token passed explicitly for the same reason as the logout call above.
    // The "Turn on notifications" banner shows again at the next sign-in.
    import('@/lib/webPush')
      .then((m) => {
        m.resetWebPushBannerDismissal();
        m.resetWebPushFailures();
        if (currentToken) return m.disableWebPush(m.staffTransport(currentToken));
      })
      .catch(() => {});

    // Clear session timeout
    clearSessionTimeout();

    // Clear session data
    setSessionStartTime(null);
    setTimeRemaining(0);

    // Clear all auth data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('session_start');
    // The member portal's way back to this session (lib/staffReturn).
    clearStaffReturn();
    // Desktop: clear the keychain token + wipe the encrypted offline DB so PHI is
    // not left on the device after logout (fire-and-forget; safe no-op on web).
    import('@/lib/authFetch').then((m) => m.clearAuthToken()).catch(() => {});
    if (typeof window !== 'undefined' && '__TAURI__' in window) {
      import('@/lib/offline/desktopOfflineService').then((m) => m.wipeOfflineDb()).catch(() => {});
    }
    // Clear persisted UI state so re-login lands on the product's default
    // surface (triveny-home for Triveny, homepage for WeaveIn) instead of
    // resuming the previous user's last tab.
    localStorage.removeItem('activeTab');
    // Offline read cache (members, enrollments… pre-cached at sign-in): member
    // data at rest in localStorage, so it goes with the session.
    clearApiCache();

    // Every other window — pop-outs, page tabs, iframe tabs, desktop windows —
    // signs out too and drops what it has on screen (TRUS-638). They do it
    // locally: no re-broadcast, no second backend logout.
    if (wasActive || currentToken) {
      broadcastSignedOut(opts?.reason ?? (scope === 'all' ? 'all-devices' : 'manual'));
    }

    clearLocalSessionState();
  };

  /**
   * What every sign-out does to THIS window's memory: the user, the cached
   * query results, the per-window reveal session — and, in a pop-out, the
   * window itself. Shared by logout() and by a sign-out learned from another
   * window (which must not touch the backend or shared storage).
   */
  const clearLocalSessionState = () => {
    // Clear member redirect flag so it works on next login
    sessionStorage.removeItem('member_redirect_handled');
    // Data-shield reveal session: per window, and PHI-unmasking.
    sessionStorage.removeItem('reveal_token');
    setToken(null);
    setUser(null);
    setPreferences(null);
    setError(null);
    // Cached query results are member data.
    queryClient.clear();
    // A pop-out window (not an iframe tab — its host tears that down) closes,
    // or shows a "signed out" screen instead of the app.
    if (isTopLevelPopoutWindow()) setPopoutSignedOut(true);

    // Dispatch event to notify other contexts (like BrandingContext) of auth state change
    window.dispatchEvent(new Event('authStateChanged'));
  };

  /** Another window signed out (or the token vanished): sign out here, locally only. */
  const signOutLocally = (reason: SignOutReason) => {
    console.warn(`🔐 Signed out in another window (${reason}); clearing this window`);
    clearSessionTimeout();
    setSessionStartTime(null);
    setTimeRemaining(0);
    // The in-memory token only. The keychain and localStorage were cleared by
    // the window that signed out; clearing them again could race a new sign-in.
    dropCachedAuthToken();
    clearLocalSessionState();
  };
  const signOutLocallyRef = useRef(signOutLocally);
  signOutLocallyRef.current = signOutLocally;

  const clearError = () => {
    setError(null);
  };

  const completeLogin = (authToken: string, userData: UserProfile, refreshToken?: string, expiresIn?: number) => {
    // Re-arm the interceptor's single-flight logout guard. It latches on the
    // first 401 of a dead session to collapse the concurrent burst; without
    // this reset, a genuine session failure LATER in this new session would be
    // silently suppressed and the user would sit on a broken page instead of
    // being returned to login.
    resetAuthFailureState();

    // Store token and session start time
    localStorage.setItem('auth_token', authToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    if (expiresIn) {
      localStorage.setItem('token_expires_at', (Date.now() + expiresIn * 1000).toString());
    }
    localStorage.setItem('session_start', Date.now().toString());

    // Sync active_cbo_id to match the authenticated user's org
    // Prevents stale cbo_id from a previous session/org leaking cross-tenant data
    if ((userData as any).cbo_id) {
      localStorage.setItem('active_cbo_id', String((userData as any).cbo_id));
    } else {
      localStorage.removeItem('active_cbo_id');
    }
    // Clear stale tenant slug — will be re-resolved from the new org context
    localStorage.removeItem('platform_tenant_slug');

    // Update state
    setToken(authToken);
    setUser(userData);
    setError(null);

    // Fetch user preferences
    fetchUserPreferences(authToken);

    // Start session timeout
    startSessionTimeout();

    // Dispatch event to notify other contexts (like BrandingContext) of auth state change
    window.dispatchEvent(new Event('authStateChanged'));
  };

  const updatePreferences = async (prefs: Partial<UserPreferences>) => {
    if (!token) {
      throw new Error('No authentication token');
    }

    try {
      console.log('📤 Updating preferences with:', prefs);
      const oldPrefs = preferences; // Store old preferences before update
      const response = await apiClient.put('/api/user/preferences', prefs);
      console.log('📥 API response:', response.data);
      setPreferences(response.data);

      // Update i18n language if changed
      if (prefs.language_code && response.data.language_code !== oldPrefs?.language_code) {
        console.log('🌍 Changing language to:', response.data.language_code);
        i18n.changeLanguage(response.data.language_code);
      }

      // Apply theme if changed - compare with old preferences
      if (prefs.theme && response.data.theme !== oldPrefs?.theme) {
        console.log('🎨 Theme changed from', oldPrefs?.theme, 'to', response.data.theme);
        applyTheme(response.data.theme);
      } else if (prefs.theme) {
        console.log('⚠️ Theme update requested but no change detected:', {
          requested: prefs.theme,
          responseTheme: response.data.theme,
          oldTheme: oldPrefs?.theme
        });
      }
    } catch (err) {
      console.error('Error updating preferences:', err);
      throw err;
    }
  };

  const refreshPreferences = async () => {
    if (token) {
      await fetchUserPreferences(token);
    }
  };

  const value: AuthContextType = useMemo(() => ({
    user,
    token,
    loading,
    error,
    preferences,
    login,
    register,
    logout,
    clearError,
    timeRemaining,
    sessionTimeoutSeconds: idleTimeoutMinutes * 60,
    sessionWarningSeconds: warningLeadSeconds(idleTimeoutMinutes * 60),
    extendSession,
    setUser,
    setToken,
    completeLogin,
    updatePreferences,
    refreshPreferences,
  }), [user, token, loading, error, preferences, timeRemaining, idleTimeoutMinutes]);

  return (
    <AuthContext.Provider value={value}>
      {popoutSignedOut ? <PopoutSignedOutScreen /> : children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Like useAuth() but returns undefined instead of throwing when there is no
 * AuthProvider. Use in components/hooks that render in BOTH the staff app
 * (wrapped in AuthProvider) and the member portal (which is not) — e.g. shared
 * date-formatting that reads the user's timezone preference.
 */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
